/** @format */

import { OptionType } from '@/types/shared';
import dynamic from 'next/dynamic';
import { FunctionComponent as FC, useContext, useMemo, useState } from 'react';
import { FieldErrors, FieldValues, UseFormRegister } from 'react-hook-form';
import { GroupBase, SingleValueProps, components } from 'react-select';
import styled, { ThemeContext } from 'styled-components';
import SelectArrow from '../../../assets/icons/select-arrow.svg';
import { Loader } from '../Loader';
import { Error } from './Error';
import { FormControl } from './FormControl';

const Select = dynamic(() => import('react-select'), {
  loading: () => <Loader />,
});

export const CustomSelect: FC<Props<FieldValues>> = ({
  options,
  showFields,
  name,
  label,
  ariaLabel = '',
  errors,
  value,
  variant = 'light',
  onChange,
  onBlur,
  isSearchable = false,
}) => {
  const [selectedValue, setSelectedValue] = useState<OptionType | null>(null);
  const alloptions = useMemo(() => options, [options]);
  const theme = useContext(ThemeContext);

  const onSelectChange = (option: OptionType | unknown) => {
    const val = option as OptionType;
    setSelectedValue(val);
    onChange && onChange(val);
  };

  const CustomSingleValue: React.ComponentType<SingleValueProps<unknown, boolean, GroupBase<unknown>>> | undefined = ({
    children,
    ...props
  }) => {
    const data = props.data as OptionType;
    return (
      <components.SingleValue {...props}>
        <span>{showFields === 'value' ? data.value : children}</span>
      </components.SingleValue>
    );
  };

  const formatOptionLabel = (option: any) => {
    return <CustomOption>{showFields === 'value' ? `${option.value}` : `${option.label}`}</CustomOption>;
  };

  const styleProxy = new Proxy(
    {},
    {
      get: (target, propKey) => () => {},
    },
  );

  return (
    <FormControl $filled={selectedValue !== null} $variant={variant}>
      <label htmlFor={name} dangerouslySetInnerHTML={{ __html: label }} />
      <CustomReactSelect
        isSearchable={isSearchable}
        className={`customReactSelect ${errors[name] ? 'error' : ''}`}
        classNamePrefix="react-select"
        aria-invalid={errors[name] ? 'true' : 'false'}
        aria-errormessage={errors[name] ? String(errors[name]?.message) : 'false'}
        options={alloptions}
        placeholder=""
        aria-label={ariaLabel}
        onChange={onSelectChange}
        formatOptionLabel={formatOptionLabel}
        components={{ SingleValue: CustomSingleValue }}
        styles={{
          control: (baseStyles, state) => ({
            ...baseStyles,
            outline: state.isFocused
              ? `2px solid ${variant === 'light' ? theme?.color.teal.light : theme?.color.teal.dark}`
              : '',
            outlineOffset: state.isFocused ? '-2px' : '',
            boxShadow: state.isFocused ? '' : '',
          }),
          singleValue: provided => ({
            ...provided,
            minHeight: '44px',
            maxHeight: '44px',
            height: '44px',
            padding: '0.50rem 7px',
            // specify a fallback color here for those values not accounted for in the styleMap
          }),
          indicatorsContainer: provided => ({
            ...provided,
            minHeight: '44px',
            maxHeight: '44px',
            height: '44px',
            // specify a fallback color here for those values not accounted for in the styleMap
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
              ? theme?.color.teal.dark
              : state.isFocused
              ? theme?.color.teal.light
              : theme?.color.white,
            color: state.isFocused || state.isSelected ? theme?.color.white : theme?.color.teal.dark,
            ':hover': {
              backgroundColor: state.isSelected ? theme?.color.teal.dark : theme?.color.teal.light,
              color: theme?.color.white,
            },
            ':active': {
              backgroundColor: state.isSelected ? theme?.color.teal.dark : theme?.color.white,
            },
          }),
        }}
      />
      {errors[name] && <Error message={String(errors[name]?.message) ?? 'error'} />}
    </FormControl>
  );
};

const CustomOption = styled.div``;

const CustomReactSelect = styled(Select)`
  &.error {
    .react-select__control {
      border: 1px solid #e56e69;
      outline-color: #e56e69 !important;
      color: #e56e69;
    }
  }

  .react-select__control {
    font-size: 0.9375rem;
    z-index: 1;
    border: 1px solid #015962;
    border-radius: 0.65rem;
    max-height: 44px;

    /* & * {
      margin: 0;
      padding: 0;
    }
    font-family: '__Figtree_2ea74a', '__Figtree_Fallback_2ea74a';


    padding: 0.65rem 0.94rem;

    height: 44px;
    background-color: #fff;
    z-index: 1;

    .react-select__value-container {
      z-index: 1;
    }
    .react-select__indicators {
      z-index: 1;
    }
    .react-select__menu-portal {
      z-index: 1;
      font-size: 0.93rem;
    }
  }
  } */
  }
  .react-select__menu {
    z-index: 2;
    width: max-content;
    minwidth: 100%;
  }
`;

type Props<T extends FieldValues> = {
  showFields: 'value' | 'label';
  options: any;
  variant?: 'light' | 'dark';
  name: keyof T;
  label: string;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  required?: boolean | string;
  onChange: (option: OptionType | null) => void;
  onBlur?: (option: OptionType | null) => void;
  isSearchable?: boolean;
  ariaLabel: string;
  value: OptionType | null | undefined;
};

const StyledSelect = styled.select`
  z-index: 1;
  option:empty {
    display: none;
  }

  // #region CUSTOM ARROW

  --right: 0.94rem;

  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  background: transparent;
  background-image: url(${SelectArrow});
  background-repeat: no-repeat;
  background-position-x: calc(100% - var(--right));
  background-position-y: 50%;

  // #endregion
`;
